import { createContext, useContext, cloneElement, isValidElement, useId } from 'react'
import type { ReactElement } from 'react'
import type { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

/**
 * The SSR gate for `<Tooltip>` (see ./tooltip.tsx). A tooltip renders nothing on the server but its
 * trigger — the popup is portalled, inert during `renderToString` — yet Base UI runs the whole machine
 * to emit that one element, measured at ~10x the bare element and 14.3% of server CPU on the report
 * page. Under the gate we emit the trigger ourselves, with the three attributes Base UI would add.
 *
 * Two signals, and both are required — see ./tooltip-ssr-gate.md for why, and for what the swap costs.
 */
const TooltipSsrGateContext = createContext(false)

export const TooltipSsrGateProvider = TooltipSsrGateContext.Provider

export function useTooltipSsrGate(): boolean {
  return useContext(TooltipSsrGateContext)
}

/** The three attributes Base UI's `Tooltip.Trigger` adds to a CLOSED trigger, in the order it adds them. */
function triggerAttributes(id: string) {
  return {
    'id': id,
    'data-base-ui-tooltip-trigger': '',
    'data-slot': 'tooltip-trigger',
  }
}

/**
 * The trigger Base UI would have rendered, without Base UI.
 *
 * Every call site in this app passes an ELEMENT as `render` and nothing else, which is the only case
 * handled. A function `render`, or none at all, throws rather than silently emitting markup that does
 * not match what Base UI produces after the swap.
 */
export function GatedTooltipTrigger({ render, ...props }: TooltipPrimitive.Trigger.Props) {
  const attributes = triggerAttributes(`base-ui-${useId()}`)

  if (!isValidElement(render)) {
    throw new Error('GatedTooltipTrigger: `render` must be an element — see tooltip-ssr-gate.tsx')
  }
  return cloneElement(render as ReactElement<Record<string, unknown>>, { ...props, ...attributes })
}
